import type { ReactNode } from "react";

import Tooltip from "@/components/ui/tooltip";

import { cx } from "@/lib/tailwind-utils";

// ─── FilterPill ─────────────────────────────────────────────────────────

interface FilterPillProps {
  children: ReactNode;
  className?: string;
  as?: React.ElementType<{ className?: string }>;
}

export function FilterPill({
  children,
  className,
  as: Component = "span",
}: FilterPillProps) {
  return (
    <Component
      className={cx(
        "inline-flex items-center rounded-full border border-primary/20 px-2.5 py-0.5 text-caption-strong",
        className,
      )}
    >
      {children}
    </Component>
  );
}

// ─── FilterPillList ─────────────────────────────────────────────────────

interface FilterPillListProps {
  items: string[];
  maxChars?: number;
  maxVisible?: number;
  renderPill?: (label: string) => ReactNode;
  renderTooltipPill?: (label: string) => ReactNode;
  overflowClassName?: string;
  className?: string;
  emptyText?: string;
}

/** Greedily fit items under a character budget, returning visible count + overflow. */
function greedyFit(items: string[], maxChars: number) {
  const all = items.join(", ");
  if (all.length <= maxChars) return items.length;

  let fitted = 0;
  let length = 0;
  for (let i = 0; i < items.length; i++) {
    const remaining = items.length - (i + 1);
    const suffix = remaining > 0 ? ` +${remaining}` : "";
    const separator = i > 0 ? ", " : "";
    const candidate =
      length + separator.length + items[i].length + suffix.length;
    if (i > 0 && candidate > maxChars) break;
    length += separator.length + items[i].length;
    fitted = i + 1;
  }
  return fitted;
}

export function FilterPillList({
  items,
  maxChars = 30,
  maxVisible,
  renderPill,
  renderTooltipPill,
  overflowClassName,
  className,
  emptyText = "-",
}: FilterPillListProps) {
  if (items.length === 0) {
    return <span className="text-foreground-secondary">{emptyText}</span>;
  }

  const defaultRenderPill = (label: string) => (
    <FilterPill key={label}>{label}</FilterPill>
  );
  const pill = renderPill ?? defaultRenderPill;
  const tooltipPill = renderTooltipPill ?? pill;

  let visibleCount = greedyFit(items, maxChars);
  if (maxVisible != null) {
    visibleCount = Math.min(visibleCount, maxVisible);
  }

  const visible = items.slice(0, visibleCount);
  const overflow = items.slice(visibleCount);

  return (
    <div className={cx("flex flex-wrap items-center gap-1", className)}>
      {visible.map((label) => pill(label))}
      {overflow.length > 0 && (
        <Tooltip
          variant="light"
          triggerAsChild
          showArrow={false}
          content={
            <div className="flex flex-wrap gap-1">
              {overflow.map((label) => tooltipPill(label))}
            </div>
          }
        >
          <span
            className={cx(
              "cursor-default rounded-full border border-border px-2 py-0.5 text-caption text-foreground-muted hover:text-foreground-secondary",
              overflowClassName,
            )}
          >
            +{overflow.length}
          </span>
        </Tooltip>
      )}
    </div>
  );
}
